import React from 'react';
import { Dialog } from '@headlessui/react';
import { useTranslation } from 'react-i18next';
import { Budget, ContributionCalculationType } from '../types/budget';
import { useUpdateBudget } from '../hooks/useBudgetQueries';

interface BudgetSettingsDialogProps {
  budget: Budget;
  isOpen: boolean;
  onClose: () => void;
  onUpdate?: (budget: Budget) => void;
}

export const BudgetSettingsDialog: React.FC<BudgetSettingsDialogProps> = ({
  budget,
  isOpen,
  onClose,
  onUpdate,
}) => {
  const { t } = useTranslation();
  const updateBudgetMutation = useUpdateBudget();

  const handleCalculationTypeChange = (type: ContributionCalculationType) => {
    const updatedBudget = {
      ...budget,
      settings: {
        ...budget.settings,
        contributionCalculationType: type,
      },
    };
    updateBudgetMutation.mutate(updatedBudget, {
      onSuccess: () => {
        if (onUpdate) {
          onUpdate(updatedBudget);
        }
      },
    });
  };

  const currentType =
    budget.settings?.contributionCalculationType || ContributionCalculationType.INCOME_RATIO;

  return (
    <Dialog open={isOpen} onClose={onClose} className="fixed inset-0 z-10 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center">
        <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30 dark:bg-opacity-50" />

        <div className="relative mx-4 w-full max-w-md rounded-lg bg-white p-8 dark:bg-gray-800">
          <Dialog.Title className="mb-4 text-lg font-medium text-gray-900 dark:text-white">
            {t('budget.settings.title')}
          </Dialog.Title>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('budget.settings.contributionMethod')}
              </label>
              <div className="mt-2 space-y-2">
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="income-ratio"
                    name="contribution-method"
                    value={ContributionCalculationType.INCOME_RATIO}
                    checked={currentType === ContributionCalculationType.INCOME_RATIO}
                    onChange={(e) =>
                      handleCalculationTypeChange(e.target.value as ContributionCalculationType)
                    }
                    className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-500 dark:border-gray-600"
                  />
                  <label
                    htmlFor="income-ratio"
                    className="ml-3 block text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    {t('budget.settings.incomeRatio')}
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="equal-split"
                    name="contribution-method"
                    value={ContributionCalculationType.EQUAL_SPLIT}
                    checked={currentType === ContributionCalculationType.EQUAL_SPLIT}
                    onChange={(e) =>
                      handleCalculationTypeChange(e.target.value as ContributionCalculationType)
                    }
                    className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-500 dark:border-gray-600"
                  />
                  <label
                    htmlFor="equal-split"
                    className="ml-3 block text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    {t('budget.settings.equalSplit')}
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="after-personal"
                    name="contribution-method"
                    value={ContributionCalculationType.AFTER_PERSONAL}
                    checked={currentType === ContributionCalculationType.AFTER_PERSONAL}
                    onChange={(e) =>
                      handleCalculationTypeChange(e.target.value as ContributionCalculationType)
                    }
                    className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-500 dark:border-gray-600"
                  />
                  <label
                    htmlFor="after-personal"
                    className="ml-3 block text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    {t('budget.settings.afterPersonal')}
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="full-balance"
                    name="contribution-method"
                    value={ContributionCalculationType.FULL_BALANCE}
                    checked={currentType === ContributionCalculationType.FULL_BALANCE}
                    onChange={(e) =>
                      handleCalculationTypeChange(e.target.value as ContributionCalculationType)
                    }
                    className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-500 dark:border-gray-600"
                  />
                  <label
                    htmlFor="full-balance"
                    className="ml-3 block text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    {t('budget.settings.fullBalance')}
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
            >
              {t('common.close')}
            </button>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
